import { Team } from '../context/WorkspaceContext';

export type Role = 'owner' | 'admin' | 'reviewer';

export interface Permission {
  canManageTeam: boolean;
  canManageProjects: boolean;
  canManageKeys: boolean;
  canManageStrings: boolean;
  canManageLanguages: boolean;
  canManageSettings: boolean;
  canManageBilling: boolean;
  canManageIntegrations: boolean;
  canManageWebhooks: boolean;
  canManageAPIKeys: boolean;
  canManageRoles: boolean;
  canRemoveUsers: boolean;
  canAddUsers: boolean;
  canEditTeamInfo: boolean;
  canViewLanguages: boolean;
  canEditLanguages: boolean;
  canViewReviews: boolean;
  canEditReviews: boolean;
  canManageReviews: boolean;
  canEditStrings: boolean;
  canDeleteTranslations: boolean;
  canManageTone: boolean;
  canManageSubscription: boolean;
  canManageAppUpdates: boolean;
}

export function getRole(team: Team | null, userId: string | undefined, profileId: string | undefined): Role {
  if (!team || !userId || !profileId) return 'reviewer';

  // Check if the user is the owner by comparing profile IDs
  if (profileId === team.owner_profile_id) return 'owner';

  // Check if the user is an admin by checking if their profile ID is in the admins array
  if (team.admins?.includes(profileId)) return 'admin';

  return 'reviewer';
}

export function getPermissions(role: Role, isOwner: boolean): Permission {
  switch (role) {
    case 'owner':
      return {
        canManageTeam: true,
        canAddUsers: true,
        canRemoveUsers: true,
        canManageRoles: true,
        canManageKeys: true,
        canEditStrings: true,
        canDeleteTranslations: true,
        canManageReviews: true,
        canManageProjects: true,
        canManageTone: true,
        canManageSubscription: true,
        canManageAppUpdates: true,
        canManageStrings: true,
        canManageLanguages: true,
        canManageSettings: true,
        canManageBilling: true,
        canManageIntegrations: true,
        canManageWebhooks: true,
        canManageAPIKeys: true,
        canEditTeamInfo: true,
        canViewLanguages: true,
        canEditLanguages: true,
        canViewReviews: true,
        canEditReviews: true
      };
    case 'admin':
      return {
        canManageTeam: false,
        canAddUsers: true,
        canRemoveUsers: true,
        canManageRoles: false,
        canManageKeys: true,
        canEditStrings: true,
        canDeleteTranslations: true,
        canManageReviews: true,
        canManageProjects: true,
        canManageTone: true,
        canManageSubscription: false,
        canManageAppUpdates: false,
        canManageStrings: true,
        canManageLanguages: false,
        canManageSettings: false,
        canManageBilling: false,
        canManageIntegrations: false,
        canManageWebhooks: false,
        canManageAPIKeys: false,
        canEditTeamInfo: false,
        canViewLanguages: true,
        canEditLanguages: true,
        canViewReviews: true,
        canEditReviews: true
      };
    case 'reviewer':
      return {
        canManageTeam: false,
        canAddUsers: false,
        canRemoveUsers: false,
        canManageRoles: false,
        canManageKeys: false,
        canEditStrings: true,
        canDeleteTranslations: false,
        canManageReviews: true,
        canManageProjects: false,
        canManageTone: false,
        canManageSubscription: false,
        canManageAppUpdates: false,
        canManageStrings: false,
        canManageLanguages: false,
        canManageSettings: false,
        canManageBilling: false,
        canManageIntegrations: false,
        canManageWebhooks: false,
        canManageAPIKeys: false,
        canEditTeamInfo: false,
        canViewLanguages: true,
        canEditLanguages: false,
        canViewReviews: true,
        canEditReviews: true
      };
    default:
      return {
        canManageTeam: false,
        canAddUsers: false,
        canRemoveUsers: false,
        canManageRoles: false,
        canManageKeys: false,
        canEditStrings: false,
        canDeleteTranslations: false,
        canManageReviews: false,
        canManageProjects: false,
        canManageTone: false,
        canManageSubscription: false,
        canManageAppUpdates: false,
        canManageStrings: false,
        canManageLanguages: false,
        canManageSettings: false,
        canManageBilling: false,
        canManageIntegrations: false,
        canManageWebhooks: false,
        canManageAPIKeys: false,
        canEditTeamInfo: false,
        canViewLanguages: false,
        canEditLanguages: false,
        canViewReviews: false,
        canEditReviews: false
      };
  }
}

export const usePermissions = (team: Team | null, userId: string | undefined, ownerProfileId: string | undefined) => {
  const role = getRole(team, userId, ownerProfileId);
  const isOwner = role === 'owner';
  const permissions = getPermissions(role, isOwner);

  return {
    role,
    permissions,
    isOwner: role === 'owner',
    isAdmin: role === 'admin',
    isReviewer: role === 'reviewer'
  };
}; 